import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Get Upload URL - Step 1 of presigned upload flow
// ============================================================================

export const GetDocumentUploadUrlRequestSchema = z.object({
  agreementId: z.uuid(),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
});

export const GetDocumentUploadUrlResponseSchema = z.object({
  documentId: z.uuid(),
  uploadUrl: z.string().url(),
});

// ============================================================================
// Confirm Document Upload - Step 2 of presigned upload flow
// ============================================================================

export const ConfirmDocumentUploadRequestSchema = z.object({
  documentId: z.uuid(),
  agreementId: z.uuid(),
  fileSizeBytes: z.number().int().min(0).optional(),
});

export const DocumentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  storagePath: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  status: z.string(),
});

export const ConfirmDocumentUploadResponseSchema = z.object({
  document: DocumentSchema,
});

// ============================================================================
// Remove Document - Remove document from agreement
// ============================================================================

export const RemoveDocumentRequestSchema = z.object({
  documentId: z.uuid(),
  agreementId: z.uuid(),
});

export const RemoveDocumentResponseSchema = z.object({
  removed: z.boolean(),
  documentDeleted: z.boolean(), // True if document was deleted from storage (no other associations)
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const agreementDocumentsContracts = {
  getUploadUrl: oc
    .input(GetDocumentUploadUrlRequestSchema)
    .output(GetDocumentUploadUrlResponseSchema)
    .route({ method: "POST" }),

  confirmUpload: oc
    .input(ConfirmDocumentUploadRequestSchema)
    .output(ConfirmDocumentUploadResponseSchema)
    .route({ method: "POST" }),

  remove: oc
    .input(RemoveDocumentRequestSchema)
    .output(RemoveDocumentResponseSchema)
    .route({ method: "POST" }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type GetDocumentUploadUrlRequest = z.infer<
  typeof GetDocumentUploadUrlRequestSchema
>;
export type GetDocumentUploadUrlResponse = z.infer<
  typeof GetDocumentUploadUrlResponseSchema
>;
export type ConfirmDocumentUploadRequest = z.infer<
  typeof ConfirmDocumentUploadRequestSchema
>;
export type ConfirmDocumentUploadResponse = z.infer<
  typeof ConfirmDocumentUploadResponseSchema
>;
export type RemoveDocumentRequest = z.infer<typeof RemoveDocumentRequestSchema>;
export type RemoveDocumentResponse = z.infer<
  typeof RemoveDocumentResponseSchema
>;
export type Document = z.infer<typeof DocumentSchema>;
